import React, { useState, useEffect } from 'react';
import { get, post, put, del } from '../utils/api';

import { useAuth } from '../context/AuthContext';

const fmt = n => '₦' + Number(n||0).toLocaleString('en-NG', {minimumFractionDigits:2, maximumFractionDigits:2});
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-NG',{day:'numeric',month:'short',year:'numeric'}) : '—';

const STATUS_COLORS = {
  active: { bg:'#EDF3FF', color:'#3B5BDB', label:'Active' },
  completed: { bg:'#E8F5EB', color:'#2E6B3A', label:'Completed' },
  defaulted: { bg:'#FDEAEA', color:'#8F2E24', label:'Defaulted' },
};

const SCHED_COLORS = {
  pending: { bg:'#F5F5F5', color:'#8A8A8A' },
  paid: { bg:'#E8F5EB', color:'#2E6B3A' },
  partial: { bg:'#FFF3DC', color:'#8B5E0A' },
  overdue: { bg:'#FDEAEA', color:'#8F2E24' },
};

const EMPTY_FORM = {
  lender_name:'', loan_type:'cash', principal_amount:'', interest_rate:'',
  interest_period:'annual', disbursement_date:new Date().toISOString().split('T')[0],
  tenure_months:'12', repayment_frequency:'monthly', has_moratorium:false,
  moratorium_months:'0', purpose:'', asset_description:'', asset_serial_number:'',
  bank_account_id:''
};

export default function Loans() {
  const { user } = useAuth();
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [paymentForm, setPaymentForm] = useState({
    payment_date: new Date().toISOString().split('T')[0],
    amount_paid:'', principal_paid:'', interest_paid:'',
    payment_method:'bank_transfer', bank_account_id:'', schedule_id:'', notes:''
  });

  const load = () => {
    setLoading(true);
    get('/loans').then(d => setLoans(d.data||[])).finally(()=>setLoading(false));
  };

  const loadSelected = (id) => {
    get('/loans/'+id).then(d => setSelected(d.data));
  };

  useEffect(() => {
    load();
    get('/bank-accounts').then(d => setBankAccounts(d.data||[]));
    // Update overdue status on load
    post('/loans/update-overdue', {}).catch(()=>{});
  }, []);

  const totalOutstanding = loans.filter(l=>l.status==='active')
    .reduce((s,l)=>s+parseFloat(l.outstanding_principal||0),0);
  const totalOverdue = loans.reduce((s,l)=>s+parseInt(l.overdue_count||0),0);

  const openEdit = () => {
    setEditForm({
      lender_name: selected.lender_name,
      loan_type: selected.loan_type,
      principal_amount: selected.principal_amount,
      interest_rate: selected.interest_rate,
      interest_period: selected.interest_period,
      disbursement_date: selected.disbursement_date?.split('T')[0],
      tenure_months: selected.tenure_months,
      repayment_frequency: selected.repayment_frequency,
      has_moratorium: selected.has_moratorium,
      moratorium_months: selected.moratorium_months,
      purpose: selected.purpose||'',
      asset_description: selected.asset_description||'',
      asset_serial_number: selected.asset_serial_number||'',
      bank_account_id: selected.bank_account_id||'',
      status: selected.status,
    });
    setShowEdit(true);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await put('/loans/'+selected.id, editForm);
      setShowEdit(false);
      loadSelected(selected.id);
      load();
    } catch(e) { alert(e.message); }
    finally { setSaving(false); }
  };

  const deleteLoan = async () => {
    try {
      await del('/loans/'+selected.id);
      setShowDelete(false);
      setSelected(null);
      load();
    } catch(e) { alert(e.message); }
  };

  const saveLoan = async () => {
    if (!form.lender_name || !form.principal_amount || !form.disbursement_date) {
      alert('Lender name, amount and disbursement date are required'); return;
    }
    setSaving(true);
    try {
      await post('/loans', form);
      setShowForm(false);
      setForm(EMPTY_FORM);
      load();
    } catch(e) { alert(e.message); }
    finally { setSaving(false); }
  };

  const recordPayment = async () => {
    if (!paymentForm.amount_paid || !paymentForm.payment_date) {
      alert('Payment date and amount required'); return;
    }
    setSaving(true);
    try {
      await post('/loans/'+selected.id+'/payments', paymentForm);
      setShowPayment(false);
      setPaymentForm({payment_date:new Date().toISOString().split('T')[0],amount_paid:'',principal_paid:'',interest_paid:'',payment_method:'bank_transfer',bank_account_id:'',schedule_id:'',notes:''});
      loadSelected(selected.id);
      load();
    } catch(e) { alert(e.message); }
    finally { setSaving(false); }
  };

  if (user?.role !== 'super_admin') return (
    <div className="empty-state"><h3>Access Restricted</h3><p>Only the Managing Director can access loan records.</p></div>
  );

  return (
    <div>
      <div className="flex-between mb-lg">
        <div>
          <h2 className="page-title">Loan Management</h2>
          <p className="text-muted text-sm">Track liabilities, repayment schedules and payments</p>
        </div>
        <button className="btn btn-primary" onClick={()=>{setForm(EMPTY_FORM);setShowForm(true);}}>+ Add Loan</button>
      </div>

      {/* Summary tiles */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:16,marginBottom:24}}>
        <div className="tile">
          <div className="tile-lbl">Total Loans</div>
          <div className="tile-val">{loans.length}</div>
        </div>
        <div className="tile">
          <div className="tile-lbl">Active Loans</div>
          <div className="tile-val warn">{loans.filter(l=>l.status==='active').length}</div>
        </div>
        <div className="tile">
          <div className="tile-lbl">Outstanding Principal</div>
          <div className="tile-val neg">{fmt(totalOutstanding)}</div>
        </div>
        <div className="tile">
          <div className="tile-lbl">Overdue Instalments</div>
          <div className="tile-val neg">{totalOverdue}</div>
        </div>
      </div>

      <div style={{display:'flex',gap:16}}>
        {/* Loans list */}
        <div style={{width:360,flexShrink:0}}>
          {loading ? <div className="spinner"/> : loans.length === 0 ? (
            <div className="empty-state"><p>No loans recorded yet</p></div>
          ) : loans.map(loan => {
            const sc = STATUS_COLORS[loan.status]||STATUS_COLORS.active;
            const isSelected = selected?.id === loan.id;
            return (
              <div key={loan.id} onClick={()=>loadSelected(loan.id)}
                style={{background:isSelected?'#F2EFE7':'#fff',border:'1px solid '+(isSelected?'#556B2F':'#E8E0D4'),
                  borderRadius:10,padding:14,marginBottom:10,cursor:'pointer'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:6}}>
                  <div>
                    <div style={{fontWeight:700,fontSize:13,color:'#3B2A21'}}>{loan.lender_name}</div>
                    <div style={{fontSize:10,color:'#8A8A8A'}}>{loan.loan_ref} · {loan.loan_type==='cash'?'💵 Cash':'🏭 Equipment'}</div>
                  </div>
                  <span style={{fontSize:9,fontWeight:700,padding:'2px 8px',borderRadius:20,
                    background:sc.bg,color:sc.color}}>{sc.label}</span>
                </div>
                <div style={{display:'flex',justifyContent:'space-between',fontSize:12}}>
                  <div><span style={{color:'#8A8A8A'}}>Principal: </span><strong>{fmt(loan.principal_amount)}</strong></div>
                  <div><span style={{color:'#8A8A8A'}}>Outstanding: </span><strong style={{color:'#8F2E24'}}>{fmt(loan.outstanding_principal)}</strong></div>
                </div>
                {loan.next_due_date && (
                  <div style={{marginTop:6,fontSize:11,color:'#8B5E0A',background:'#FFF3DC',borderRadius:4,padding:'3px 8px',display:'inline-block'}}>
                    Next due: {fmtDate(loan.next_due_date)} — {fmt(loan.next_due_amount)}
                  </div>
                )}
                {parseInt(loan.overdue_count) > 0 && (
                  <div style={{marginTop:4,fontSize:11,color:'#8F2E24',fontWeight:600}}>
                    ⚠ {loan.overdue_count} overdue instalment{loan.overdue_count>1?'s':''}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Loan detail */}
        {selected && (
          <div style={{flex:1,background:'#fff',border:'1px solid #E8E0D4',borderRadius:12,padding:24,overflowY:'auto',maxHeight:'75vh'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:20}}>
              <div>
                <h3 style={{fontFamily:'Georgia,serif',color:'#3B2A21',marginBottom:4}}>{selected.lender_name}</h3>
                <div style={{fontSize:12,color:'#8A8A8A'}}>{selected.loan_ref} · {selected.loan_type==='cash'?'Cash Loan':'Equipment Loan'}</div>
              </div>
              <div style={{display:'flex',gap:8}}>
                {selected.status === 'active' && (
                  <button className="btn btn-primary btn-sm" onClick={()=>setShowPayment(true)}>+ Record Payment</button>
                )}
                <button className="btn btn-outline btn-sm" onClick={()=>openEdit()}>✏️ Edit</button>
                <button className="btn btn-outline btn-sm" style={{color:'#8F2E24',borderColor:'#8F2E24'}} onClick={()=>setShowDelete(true)}>🗑 Delete</button>
                <button className="btn btn-ghost btn-sm" onClick={()=>setSelected(null)}>✕</button>
              </div>
            </div>

            {/* Loan details */}
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12,marginBottom:20}}>
              {[
                ['Principal', fmt(selected.principal_amount)],
                ['Interest Rate', selected.interest_rate+'% '+selected.interest_period],
                ['Tenure', selected.tenure_months+' months'],
                ['Disbursed', fmtDate(selected.disbursement_date)],
                ['Frequency', selected.repayment_frequency],
                ['Moratorium', selected.has_moratorium ? selected.moratorium_months+' months' : 'None'],
                ['Total Paid', fmt((selected.payments||[]).reduce((s,p)=>s+parseFloat(p.amount_paid),0))],
                ['Outstanding', fmt(selected.principal_amount - (selected.payments||[]).reduce((s,p)=>s+parseFloat(p.principal_paid||0),0))],
                ['Status', STATUS_COLORS[selected.status]?.label || selected.status],
              ].map(([label,val])=>(
                <div key={label} style={{background:'#F9F8F5',borderRadius:8,padding:10}}>
                  <div style={{fontSize:10,color:'#8A8A8A',textTransform:'uppercase',letterSpacing:'.05em',marginBottom:4}}>{label}</div>
                  <div style={{fontSize:13,fontWeight:600,color:'#3B2A21'}}>{val}</div>
                </div>
              ))}
            </div>

            {selected.purpose && (
              <div style={{background:'#F2EFE7',borderRadius:8,padding:12,marginBottom:16,fontSize:12,color:'#556B2F'}}>
                <strong>Purpose:</strong> {selected.purpose}
              </div>
            )}

            {selected.asset_description && (
              <div style={{background:'#EDF3FF',borderRadius:8,padding:12,marginBottom:16,fontSize:12,color:'#3B5BDB'}}>
                <strong>Asset:</strong> {selected.asset_description}
                {selected.asset_serial_number && ` · S/N: ${selected.asset_serial_number}`}
              </div>
            )}

            {/* Repayment Schedule */}
            <div style={{fontWeight:700,fontSize:12,color:'#556B2F',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:10}}>
              Repayment Schedule
            </div>
            <div style={{overflowX:'auto',marginBottom:20}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                <thead><tr style={{background:'#556B2F'}}>
                  {['#','Due Date','Type','Principal','Interest','Total','Paid','Status'].map(h=>(
                    <th key={h} style={{padding:'7px 10px',color:'#F2EFE7',textAlign:h==='#'||h==='Type'||h==='Status'?'center':'right',fontSize:10,textTransform:'uppercase'}}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {(selected.schedule||[]).map((s,i)=>{
                    const sc = SCHED_COLORS[s.status]||SCHED_COLORS.pending;
                    return (
                      <tr key={s.id} style={{background:i%2===0?'#fff':'#F9F8F5'}}>
                        <td style={{padding:'7px 10px',textAlign:'center',color:'#8A8A8A'}}>{s.instalment_number}</td>
                        <td style={{padding:'7px 10px',textAlign:'right'}}>{fmtDate(s.due_date)}</td>
                        <td style={{padding:'7px 10px',textAlign:'center',fontSize:10,color:s.is_moratorium_period?'#3B5BDB':'#556B2F'}}>
                          {s.is_moratorium_period?'Interest Only':'Capital+Int'}
                        </td>
                        <td style={{padding:'7px 10px',textAlign:'right'}}>{fmt(s.principal_due)}</td>
                        <td style={{padding:'7px 10px',textAlign:'right',color:'#8B5E0A'}}>{fmt(s.interest_due)}</td>
                        <td style={{padding:'7px 10px',textAlign:'right',fontWeight:700}}>{fmt(s.total_due)}</td>
                        <td style={{padding:'7px 10px',textAlign:'right',color:'#2E6B3A'}}>{fmt(s.amount_paid)}</td>
                        <td style={{padding:'7px 10px',textAlign:'center'}}>
                          <span style={{fontSize:9,fontWeight:700,padding:'2px 8px',borderRadius:20,background:sc.bg,color:sc.color}}>
                            {s.status.toUpperCase()}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Payment history */}
            {(selected.payments||[]).length > 0 && (
              <>
                <div style={{fontWeight:700,fontSize:12,color:'#556B2F',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:10}}>
                  Payment History
                </div>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                  <thead><tr style={{background:'#A8893D'}}>
                    {['Date','Amount','Principal','Interest','Method','Notes'].map(h=>(
                      <th key={h} style={{padding:'7px 10px',color:'#fff',textAlign:'left',fontSize:10}}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {(selected.payments||[]).map((p,i)=>(
                      <tr key={p.id} style={{background:i%2===0?'#fff':'#F9F8F5'}}>
                        <td style={{padding:'7px 10px'}}>{fmtDate(p.payment_date)}</td>
                        <td style={{padding:'7px 10px',fontWeight:700,color:'#2E6B3A'}}>{fmt(p.amount_paid)}</td>
                        <td style={{padding:'7px 10px'}}>{fmt(p.principal_paid)}</td>
                        <td style={{padding:'7px 10px',color:'#8B5E0A'}}>{fmt(p.interest_paid)}</td>
                        <td style={{padding:'7px 10px',color:'#8A8A8A'}}>{p.payment_method||'—'}</td>
                        <td style={{padding:'7px 10px',color:'#8A8A8A'}}>{p.notes||'—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
        )}
      </div>

      {/* Edit Loan Modal */}
      {showEdit && selected && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:580}}>
            <div className="modal-hdr" style={{background:'#3B2A21',borderRadius:'10px 10px 0 0'}}>
              <span className="modal-title" style={{color:'#A8893D',fontFamily:'Georgia,serif'}}>Edit Loan</span>
              <button className="btn btn-ghost btn-sm" style={{color:'#F2EFE7'}} onClick={()=>setShowEdit(false)}>✕</button>
            </div>
            <div className="modal-body" style={{maxHeight:'75vh',overflowY:'auto'}}>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Lender Name</label>
                  <input className="form-control" value={editForm.lender_name||''} onChange={e=>setEditForm({...editForm,lender_name:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Loan Type</label>
                  <select className="form-control" value={editForm.loan_type||'cash'} onChange={e=>setEditForm({...editForm,loan_type:e.target.value})}>
                    <option value="cash">💵 Cash Loan</option>
                    <option value="equipment">🏭 Equipment Loan</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Principal Amount (₦)</label>
                  <input type="number" className="form-control" value={editForm.principal_amount||''} onChange={e=>setEditForm({...editForm,principal_amount:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Disbursement Date</label>
                  <input type="date" className="form-control" value={editForm.disbursement_date||''} onChange={e=>setEditForm({...editForm,disbursement_date:e.target.value})} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Interest Rate (%)</label>
                  <input type="number" step="0.01" className="form-control" value={editForm.interest_rate||''} onChange={e=>setEditForm({...editForm,interest_rate:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Interest Period</label>
                  <select className="form-control" value={editForm.interest_period||'annual'} onChange={e=>setEditForm({...editForm,interest_period:e.target.value})}>
                    <option value="daily">Per Day</option>
                    <option value="weekly">Per Week</option>
                    <option value="annual">Per Annum</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Tenure (months)</label>
                  <input type="number" className="form-control" value={editForm.tenure_months||''} onChange={e=>setEditForm({...editForm,tenure_months:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Repayment Frequency</label>
                  <select className="form-control" value={editForm.repayment_frequency||'monthly'} onChange={e=>setEditForm({...editForm,repayment_frequency:e.target.value})}>
                    <option value="monthly">Monthly</option>
                    <option value="weekly">Weekly</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select className="form-control" value={editForm.status||'active'} onChange={e=>setEditForm({...editForm,status:e.target.value})}>
                    <option value="active">Active</option>
                    <option value="completed">Completed</option>
                    <option value="defaulted">Defaulted</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Bank Account</label>
                  <select className="form-control" value={editForm.bank_account_id||''} onChange={e=>setEditForm({...editForm,bank_account_id:e.target.value})}>
                    <option value="">Select account...</option>
                    {bankAccounts.map(b=><option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',fontSize:13,fontWeight:600,color:'#3B2A21'}}>
                  <input type="checkbox" checked={editForm.has_moratorium||false} onChange={e=>setEditForm({...editForm,has_moratorium:e.target.checked})} style={{width:16,height:16}} />
                  Has moratorium period
                </label>
              </div>
              {editForm.has_moratorium && (
                <div className="form-group">
                  <label className="form-label">Moratorium Duration (months)</label>
                  <input type="number" className="form-control" value={editForm.moratorium_months||0} onChange={e=>setEditForm({...editForm,moratorium_months:e.target.value})} />
                </div>
              )}
              {editForm.loan_type === 'equipment' && (
                <>
                  <div className="form-group">
                    <label className="form-label">Asset Description</label>
                    <input className="form-control" value={editForm.asset_description||''} onChange={e=>setEditForm({...editForm,asset_description:e.target.value})} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Serial Number</label>
                    <input className="form-control" value={editForm.asset_serial_number||''} onChange={e=>setEditForm({...editForm,asset_serial_number:e.target.value})} />
                  </div>
                </>
              )}
              <div className="form-group">
                <label className="form-label">Purpose / Notes</label>
                <textarea className="form-control" rows={2} value={editForm.purpose||''} onChange={e=>setEditForm({...editForm,purpose:e.target.value})} />
              </div>
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setShowEdit(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveEdit} disabled={saving}>{saving?'Saving...':'Save Changes'}</button>
            </div>
          </div>
        </div>
      )}
      {/* Delete Loan Modal */}
      {showDelete && selected && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:400}}>
            <div className="modal-hdr" style={{background:'#8F2E24',borderRadius:'10px 10px 0 0'}}>
              <span className="modal-title" style={{color:'#fff'}}>Delete Loan</span>
              <button className="btn btn-ghost btn-sm" style={{color:'#fff'}} onClick={()=>setShowDelete(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{background:'#FDEAEA',borderRadius:8,padding:14,marginBottom:14,fontSize:13,color:'#8F2E24'}}>
                <strong>⚠ This action cannot be undone.</strong>
              </div>
              <p style={{fontSize:13,color:'#3B2A21',marginBottom:8}}>
                Are you sure you want to delete the loan from <strong>{selected.lender_name}</strong>?
              </p>
              <p style={{fontSize:12,color:'#8A8A8A'}}>
                This will delete the loan record, all scheduled instalments and all payment history.
                Any cashbook entries created from this loan will remain.
              </p>
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setShowDelete(false)}>Cancel</button>
              <button className="btn btn-danger" onClick={deleteLoan}>Delete Loan</button>
            </div>
          </div>
        </div>
      )}
      {/* Add Loan Modal */}
      {showForm && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:580}}>
            <div className="modal-hdr" style={{background:'#3B2A21',borderRadius:'10px 10px 0 0'}}>
              <span className="modal-title" style={{color:'#A8893D',fontFamily:'Georgia,serif'}}>Record New Loan</span>
              <button className="btn btn-ghost btn-sm" style={{color:'#F2EFE7'}} onClick={()=>setShowForm(false)}>✕</button>
            </div>
            <div className="modal-body" style={{maxHeight:'75vh',overflowY:'auto'}}>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Lender Name *</label>
                  <input className="form-control" value={form.lender_name} onChange={e=>setForm({...form,lender_name:e.target.value})} placeholder="e.g. First Bank, Alhaji Musa" />
                </div>
                <div className="form-group">
                  <label className="form-label">Loan Type *</label>
                  <select className="form-control" value={form.loan_type} onChange={e=>setForm({...form,loan_type:e.target.value})}>
                    <option value="cash">💵 Cash Loan</option>
                    <option value="equipment">🏭 Equipment Loan</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Principal Amount (₦) *</label>
                  <input type="number" className="form-control" value={form.principal_amount} onChange={e=>setForm({...form,principal_amount:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Disbursement Date *</label>
                  <input type="date" className="form-control" value={form.disbursement_date} onChange={e=>setForm({...form,disbursement_date:e.target.value})} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Interest Rate (%)</label>
                  <input type="number" step="0.01" className="form-control" value={form.interest_rate} onChange={e=>setForm({...form,interest_rate:e.target.value})} placeholder="e.g. 2.5" />
                </div>
                <div className="form-group">
                  <label className="form-label">Interest Period</label>
                  <select className="form-control" value={form.interest_period} onChange={e=>setForm({...form,interest_period:e.target.value})}>
                    <option value="daily">Per Day</option>
                    <option value="weekly">Per Week</option>
                    <option value="annual">Per Annum</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Tenure (months) *</label>
                  <input type="number" className="form-control" value={form.tenure_months} onChange={e=>setForm({...form,tenure_months:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Repayment Frequency</label>
                  <select className="form-control" value={form.repayment_frequency} onChange={e=>setForm({...form,repayment_frequency:e.target.value})}>
                    <option value="monthly">Monthly</option>
                    <option value="weekly">Weekly</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',fontSize:13,fontWeight:600,color:'#3B2A21'}}>
                  <input type="checkbox" checked={form.has_moratorium} onChange={e=>setForm({...form,has_moratorium:e.target.checked})} style={{width:16,height:16}} />
                  This loan has a moratorium period
                </label>
                <div className="form-hint">During moratorium, only interest is paid. Capital repayment begins after.</div>
              </div>
              {form.has_moratorium && (
                <div className="form-group">
                  <label className="form-label">Moratorium Duration (months)</label>
                  <input type="number" className="form-control" value={form.moratorium_months} onChange={e=>setForm({...form,moratorium_months:e.target.value})} />
                </div>
              )}
              {form.loan_type === 'cash' && (
                <div className="form-group">
                  <label className="form-label">Bank Account Received Into</label>
                  <select className="form-control" value={form.bank_account_id} onChange={e=>setForm({...form,bank_account_id:e.target.value})}>
                    <option value="">Select account...</option>
                    {bankAccounts.map(b=><option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </div>
              )}
              {form.loan_type === 'equipment' && (
                <>
                  <div className="form-group">
                    <label className="form-label">Asset Description</label>
                    <input className="form-control" value={form.asset_description} onChange={e=>setForm({...form,asset_description:e.target.value})} placeholder="e.g. Industrial Sewing Machine" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Serial Number</label>
                    <input className="form-control" value={form.asset_serial_number} onChange={e=>setForm({...form,asset_serial_number:e.target.value})} />
                  </div>
                </>
              )}
              <div className="form-group">
                <label className="form-label">Purpose / Notes</label>
                <textarea className="form-control" rows={2} value={form.purpose} onChange={e=>setForm({...form,purpose:e.target.value})} />
              </div>
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setShowForm(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveLoan} disabled={saving}>
                {saving?'Saving...':'Save Loan & Generate Schedule'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Record Payment Modal */}
      {showPayment && selected && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:460}}>
            <div className="modal-hdr">
              <span className="modal-title">Record Loan Payment</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowPayment(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{background:'#F2EFE7',borderRadius:8,padding:10,marginBottom:14,fontSize:12,color:'#556B2F'}}>
                <strong>{selected.lender_name}</strong> · {selected.loan_ref}
              </div>
              <div className="form-group">
                <label className="form-label">Link to Schedule Instalment</label>
                <select className="form-control" value={paymentForm.schedule_id} onChange={e=>setPaymentForm({...paymentForm,schedule_id:e.target.value})}>
                  <option value="">— Select instalment (optional) —</option>
                  {(selected.schedule||[]).filter(s=>s.status!=='paid').map(s=>(
                    <option key={s.id} value={s.id}>
                      #{s.instalment_number} · {fmtDate(s.due_date)} · {fmt(s.total_due)} · {s.status.toUpperCase()}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Payment Date *</label>
                  <input type="date" className="form-control" value={paymentForm.payment_date} onChange={e=>setPaymentForm({...paymentForm,payment_date:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Total Amount Paid (₦) *</label>
                  <input type="number" className="form-control" value={paymentForm.amount_paid} onChange={e=>setPaymentForm({...paymentForm,amount_paid:e.target.value})} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Principal Portion (₦)</label>
                  <input type="number" className="form-control" value={paymentForm.principal_paid} onChange={e=>setPaymentForm({...paymentForm,principal_paid:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Interest Portion (₦)</label>
                  <input type="number" className="form-control" value={paymentForm.interest_paid} onChange={e=>setPaymentForm({...paymentForm,interest_paid:e.target.value})} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Payment Method</label>
                  <select className="form-control" value={paymentForm.payment_method} onChange={e=>setPaymentForm({...paymentForm,payment_method:e.target.value})}>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="cash">Cash</option>
                    <option value="pos">POS</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Bank Account</label>
                  <select className="form-control" value={paymentForm.bank_account_id} onChange={e=>setPaymentForm({...paymentForm,bank_account_id:e.target.value})}>
                    <option value="">Select account...</option>
                    {bankAccounts.map(b=><option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <input className="form-control" value={paymentForm.notes} onChange={e=>setPaymentForm({...paymentForm,notes:e.target.value})} />
              </div>
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setShowPayment(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={recordPayment} disabled={saving}>
                {saving?'Saving...':'Record Payment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
